import { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { getPortalRequestInit } from '@edison/api-client-react';
import type { PortalMode } from '@/store/portal-store.js';
import { PortalMarkdown } from './portal-markdown.js';

// Frontend mirror of @edison/db's ArtefactCitation (the frontend does not import the backend
// lib). quote === storedContent.slice(charStart, charEnd) — UTF-16 code-unit offsets into the
// FULL stored artefact content. line is the 1-based line of charStart.
export interface ArtefactCitation {
  artefactId: string;
  quote: string;
  charStart: number;
  charEnd: number;
  line: number;
}

export interface ArtefactRef {
  id: string;
  title: string;
  type: string;
  contentHash?: string | null;
  createdAt?: string;
  citation?: ArtefactCitation;
}

interface Props {
  artefact: ArtefactRef | null;
  token: string;
  mode: PortalMode;
  onClose: () => void;
  citation?: ArtefactCitation;
}

export function PortalArtefactPanel({ artefact, token, mode, onClose, citation: citationProp }: Props) {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!artefact) return;
    setIsLoading(true);
    setContent(null);
    setError(null);
    getPortalRequestInit(token, mode)
      .then(({ base, headers }) =>
        fetch(`${base}/evidence/${artefact.id}`, { headers }).then((r) => r.json())
      )
      .then((body: { data?: { content?: string }; error?: { message: string } }) => {
        if (body.error) throw new Error(body.error.message);
        setContent(body.data?.content ?? null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setIsLoading(false));
  }, [artefact, token, mode]);

  // Explicit prop wins, else fall back to the citation carried on the ArtefactRef itself.
  const citation = citationProp ?? artefact?.citation;

  // isMarkdown must be safe to compute before the artefact-null early return below (hooks that
  // depend on it are declared above that return, per Rules of Hooks).
  const isMarkdown =
    !!artefact &&
    (artefact.type === 'prd_document' ||
      artefact.type === 'manual_note' ||
      artefact.type === 'meeting_transcript' ||
      artefact.type === 'llm_session' ||
      artefact.type === 'technical_spec' ||
      artefact.type === 'file_upload' ||
      artefact.title.endsWith('.md'));

  // Non-markdown branch: highlight EXACTLY using offsets, but only when they still line up with
  // the currently stored content (the artefact may have changed since the citation was recorded).
  const offsetsValid = Boolean(
    !isMarkdown &&
      citation &&
      content !== null &&
      citation.charStart >= 0 &&
      citation.charEnd <= content.length &&
      citation.charStart < citation.charEnd &&
      content.slice(citation.charStart, citation.charEnd) === citation.quote
  );

  const markRef = useRef<HTMLElement>(null);
  const mdContainerRef = useRef<HTMLDivElement>(null);
  const highlightedElRef = useRef<HTMLElement | null>(null);

  // Autoscroll — monospace exact-offset branch. Best-effort: failure is silent, the pinned
  // "Cited passage" card above is the guaranteed deliverable.
  useEffect(() => {
    if (isMarkdown || !offsetsValid || !markRef.current) return;
    try {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      markRef.current.scrollIntoView({ block: 'center', behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    } catch {
      // best-effort only
    }
  }, [content, citation, isMarkdown, offsetsValid]);

  // Best-effort highlight + autoscroll — markdown branch. Walks the rendered PortalMarkdown DOM
  // looking for a text node containing a whitespace-normalised anchor of the quote. If formatting
  // split the text across nodes, nothing matches and we do nothing (no error, no console noise).
  useEffect(() => {
    if (!isMarkdown || !content || !citation || !mdContainerRef.current) return;
    const container = mdContainerRef.current;
    try {
      const normalize = (s: string) => s.replace(/\s+/g, ' ').trim();
      const quoteNorm = normalize(citation.quote);
      if (!quoteNorm) return;
      const anchor = quoteNorm.length > 60 ? quoteNorm.slice(0, 60) : quoteNorm;
      if (!anchor) return;

      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      let node: Node | null;
      while ((node = walker.nextNode())) {
        const normText = normalize(node.textContent ?? '');
        if (normText.includes(anchor)) {
          const parent = node.parentElement;
          if (parent) {
            parent.classList.add('bg-amber-500/20', 'ring-1', 'ring-amber-400/50', 'rounded');
            highlightedElRef.current = parent;
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            parent.scrollIntoView({ block: 'center', behavior: prefersReducedMotion ? 'auto' : 'smooth' });
          }
          break;
        }
      }
    } catch {
      // best-effort only — no match found or DOM walk failed
    }

    return () => {
      if (highlightedElRef.current) {
        highlightedElRef.current.classList.remove('bg-amber-500/20', 'ring-1', 'ring-amber-400/50', 'rounded');
        highlightedElRef.current = null;
      }
    };
  }, [content, citation, isMarkdown]);

  if (!artefact) return null;

  let formattedDate: string | null = null;
  if (artefact.createdAt) {
    const parsed = new Date(artefact.createdAt);
    if (!Number.isNaN(parsed.getTime())) {
      formattedDate = parsed.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-y-0 right-0 w-[540px] max-w-full bg-gray-900 border-l border-gray-800 flex flex-col z-50 shadow-2xl">
        {/* Header */}
        <div className="flex items-start gap-3 px-6 py-4 border-b border-gray-800 shrink-0">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
              {artefact.type.replace(/_/g, ' ')}
            </p>
            <h2 className="text-sm font-semibold text-white leading-snug">{artefact.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-md p-1.5 text-gray-500 hover:text-gray-300 hover:bg-gray-800 transition-colors mt-0.5"
            aria-label="Close evidence panel"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Pinned cited passage — always shown when a citation exists, independent of fetch
              state, so it never fails even if the document body can't be highlighted. */}
          {citation && (
            <section
              aria-label="Cited passage"
              className="mb-4 rounded-lg border border-gray-700 bg-gray-800/60 px-4 py-3"
            >
              <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mb-1.5">
                Cited passage
              </p>
              <p className="text-sm leading-relaxed text-gray-100 mb-2">
                <mark className="rounded bg-amber-500/25 px-0.5 text-amber-100">{citation.quote}</mark>
              </p>
              <p className="text-[11px] text-gray-500">
                {[artefact.title, formattedDate, `Line ${citation.line}`].filter(Boolean).join(' · ')}
              </p>
            </section>
          )}
          {isLoading && (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading evidence…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-900/50 bg-red-950/40 px-4 py-3 text-sm text-red-300">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              {error}
            </div>
          )}
          {content && (
            isMarkdown ? (
              <div ref={mdContainerRef}>
                <PortalMarkdown content={content} />
              </div>
            ) : offsetsValid && citation ? (
              <pre className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed font-mono">
                {content.slice(0, citation.charStart)}
                <mark ref={markRef} className="rounded bg-amber-500/25 px-0.5 text-amber-100">
                  {content.slice(citation.charStart, citation.charEnd)}
                </mark>
                {content.slice(citation.charEnd)}
              </pre>
            ) : (
              <pre className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed font-mono">{content}</pre>
            )
          )}
          {!isLoading && !error && !content && (
            <p className="text-sm text-gray-500">No content available for this artefact.</p>
          )}
        </div>
      </div>
    </>
  );
}
